package com.solevo.dist.pages.statements

import androidx.compose.runtime.*
import com.solevo.dist.components.CenteredLoader
import com.solevo.dist.components.DashboardPageLayout
import com.solevo.dist.components.RequireAuth
import com.solevo.dist.components.WaitForAuthAndViewModel
import com.solevo.dist.viewmodels.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import com.solevo.dist.i18n.LocalStrings
import com.solevo.dist.util.Res
import com.solevo.dist.util.withAlpha
import com.solevo.dist.util.DateFormatter
import com.solevo.dist.components.StatusPill
import com.solevo.dist.components.dialogs.ErrorDialog
import com.solevo.dist.theme.styles.tableHeaderCell
import com.solevo.dist.theme.styles.tableBodyCell
import com.solevo.dist.theme.styles.tableBodyCellBold
import com.solevo.dist.theme.styles.tableRow
import com.solevo.dist.theme.styles.tableHeaderRow
import com.solevo.dist.theme.styles.sectionTitle
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Text
import kotlinx.datetime.Clock
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.web.css.*

@Page("/statement")
@Composable
fun StatementScreen() { RequireAuth { StatementPage() } }

@Composable
fun StatementPage() {
    WaitForAuthAndViewModel(
        key = "statements",
        viewModelFactory = { StatementsViewModel() },
        onViewModelReady = { vm -> vm.start() }
    ) { vm ->
        DisposableEffect(Unit) { onDispose { vm.stop() } }
        val ui by vm.state.collectAsState()
        var errorMessage by remember { mutableStateOf<String?>(null) }

        // Initialize default dates if not set: start = today - 1 year, end = today
        val today = remember { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date }
        val defaultStart = remember(today) { today.minus(DatePeriod(years = 1)) }
        LaunchedEffect(ui.startDate, ui.endDate) {
            if (ui.startDate == null) vm.setStartDate(defaultStart)
            if (ui.endDate == null) vm.setEndDate(today)
        }

        DashboardPageLayout(header = {
            StatementHeader(
                ui = ui,
                defaultStart = defaultStart,
                defaultEnd = today,
                onTabSelected = { vm.selectTab(it) },
                onIncludeOpenOrders = { vm.setIncludeOpenOrders(it) },
                onStartDateChange = { vm.setStartDate(it) },
                onEndDateChange = { vm.setEndDate(it) },
                onApply = { s, e -> vm.applyFilters(s, e) },
                onInvalidRange = { errorMessage = it }
            )
        }) {
            StatementBody(ui)
        }

        errorMessage?.let { msg ->
            ErrorDialog(message = msg) { errorMessage = null }
        }
    }
}

@Composable private fun VSpace(h: CSSLengthValue) = Box(Modifier.height(h))

@Composable
private fun StatementHeader(
    ui: StatementsUiState,
    defaultStart: LocalDate,
    defaultEnd: LocalDate,
    onTabSelected: (StatementsTab) -> Unit,
    onIncludeOpenOrders: (Boolean) -> Unit,
    onStartDateChange: (LocalDate?) -> Unit,
    onEndDateChange: (LocalDate?) -> Unit,
    onApply: (LocalDate, LocalDate) -> Unit,
    onInvalidRange: (String) -> Unit
) {
    Column(Modifier.fillMaxWidth().padding(24.px)) {
        val strings = LocalStrings.current
        SpanText(strings.statementTitle, Modifier.sectionTitle())
        VSpace(16.px)

        // Two-column header: left summary (50%), right controls (50%)
        Row(Modifier.fillMaxWidth().gap(24.px)) {
            // Left: summary list with underlines
            Column(Modifier.flex(1, 1, 0.px)) {
                val s = LocalStrings.current
                SummaryRow(s.statementAvailableCredit, ui.header.availableText)
                SummaryRow(s.statementTotalCreditLine, ui.header.totalLineText)
                SummaryRow(s.statementOpenInvoices, ui.header.openInvoicesText)
                SummaryRow(s.statementGoodsInDelivery, ui.header.goodsInDeliveryText)
                // underline after last row per request
                Box(Modifier.fillMaxWidth().height(1.px).backgroundColor(rgb(240, 242, 246)))
            }

            // Right: controls aligned to right
            Column(Modifier.flex(1, 1, 0.px).alignItems(AlignItems.End)) {
                // Include open orders checkbox at top-right
                Row(Modifier.alignItems(AlignItems.Center).gap(8.px).cursor(Cursor.Pointer).onClick { onIncludeOpenOrders(!ui.includeOpenOrders) }) {
                    Box(
                        Modifier
                            .size(14.px)
                            .border(1.px, LineStyle.Solid, rgb(210, 214, 219))
                            .borderRadius(3.px)
                            .backgroundColor(if (ui.includeOpenOrders) rgb(30, 100, 255) else rgb(255, 255, 255))
                    )
                    SpanText(strings.includeOpenOrders, Modifier.fontSize(13.px).color(rgb(36, 41, 47)))
                }
            }
        }

        // Date pickers positioned below summaries but right-aligned with Apply button
        val startValue = ui.startDate ?: defaultStart
        val endValue = ui.endDate ?: defaultEnd

        Row(Modifier.fillMaxWidth().justifyContent(JustifyContent.FlexEnd)) {
            Row(Modifier.gap(16.px).alignItems(AlignItems.End)) {
                Column(Modifier.alignItems(AlignItems.End)) {
                    SpanText(strings.startDate, Modifier.fontSize(12.px).color(rgb(140, 149, 159)))
                    Input(InputType.Date) {
                        value(DateFormatter.formatDateInput(startValue))
                        onInput { ev ->
                            val v = ev.value
                            val ld = v.takeIf { it.isNotBlank() }?.split("-")?.mapNotNull { it.toIntOrNull() }?.takeIf { it.size == 3 }?.let { LocalDate(it[0], it[1], it[2]) }
                            val effectiveEnd = ui.endDate ?: defaultEnd
                            if (ld != null && ld > effectiveEnd) {
                                onInvalidRange("Start date cannot be after end date.")
                                return@onInput
                            }
                            val newStart = ld ?: defaultStart
                            onStartDateChange(newStart)
                        }
                        style {
                            backgroundColor(rgb(245, 247, 250))
                            border {
                                width(1.px)
                                style(LineStyle.Solid)
                                color(rgb(230, 232, 235))
                            }
                            borderRadius(8.px)
                            padding(6.px, 10.px)
                        }
                    }
                }
                Column(Modifier.alignItems(AlignItems.End)) {
                    SpanText(strings.endDate, Modifier.fontSize(12.px).color(rgb(140, 149, 159)))
                    Input(InputType.Date) {
                        value(DateFormatter.formatDateInput(endValue))
                        onInput { ev ->
                            val v = ev.value
                            val ld = v.takeIf { it.isNotBlank() }?.split("-")?.mapNotNull { it.toIntOrNull() }?.takeIf { it.size == 3 }?.let { LocalDate(it[0], it[1], it[2]) }
                            val effectiveStart = ui.startDate ?: defaultStart
                            if (ld != null && ld < effectiveStart) {
                                onInvalidRange("End date cannot be before start date.")
                                return@onInput
                            }
                            val newEnd = ld ?: defaultEnd
                            onEndDateChange(newEnd)
                        }
                        style {
                            backgroundColor(rgb(245, 247, 250))
                            border {
                                width(1.px)
                                style(LineStyle.Solid)
                                color(rgb(230, 232, 235))
                            }
                            borderRadius(8.px)
                            padding(6.px, 10.px)
                        }
                    }
                }
                // Apply button
                Box(Modifier.alignItems(AlignItems.End)) {
                    Row(
                        Modifier
                            .alignItems(AlignItems.Center)
                            .backgroundColor(rgb(24, 119, 242))
                            .color(rgb(255, 255, 255))
                            .padding(leftRight = 16.px, topBottom = 10.px)
                            .borderRadius(20.px)
                            .cursor(Cursor.Pointer)
                            .onClick {
                                onApply(ui.startDate ?: defaultStart, ui.endDate ?: defaultEnd)
                            }
                    ) {
                        SpanText(strings.apply, Modifier.color(rgb(255, 255, 255)).fontSize(14.px).fontWeight(FontWeight.SemiBold))
                    }
                }
            }
        }

        VSpace(16.px)

        // Tabs
        Box(
            Modifier
                .fillMaxWidth()
                .position(Position.Relative)
        ) {
            Box(
                Modifier
                    .position(Position.Absolute)
                    .left(0.px)
                    .right(0.px)
                    .bottom(1.px)
                    .height(1.px)
                    .backgroundColor(rgb(230, 232, 235))
                    .zIndex(0)
            )

            Row(
                Modifier
                    .gap(24.px)
                    .align(Alignment.BottomStart)
                    .zIndex(1)
            ) {
                StatementsTab.values().forEach { t ->
                    val s = LocalStrings.current
                    TopTab(
                        label = if (t == StatementsTab.All) s.statementTabAll else s.statementTabOpen,
                        selected = ui.selectedTab == t,
                        onClick = { onTabSelected(t) }
                    )
                }
            }
        }
    }
}

@Composable
private fun TopTab(label: String, selected: Boolean, onClick: () -> Unit) {
    Box(
        Modifier
            .cursor(Cursor.Pointer)
            .padding(leftRight = 12.px, topBottom = 10.px)
            .styleModifier { if (selected) property("box-shadow", "inset 0 -3px 0 0 black") }
            .onClick { onClick() }
    ) {
        SpanText(
            label,
            Modifier
                .fontSize(15.px)
                .fontWeight(if (selected) FontWeight.Bold else FontWeight.Normal)
                .color(rgb(36, 41, 47))
        )
    }
}

@Composable
private fun SummaryRow(label: String, value: String, last: Boolean = false) {
    Column(Modifier.fillMaxWidth()) {
        Row(Modifier.fillMaxWidth().padding(topBottom = 12.px)) {
            SpanText(label, Modifier.fontSize(13.px).color(rgb(120, 130, 140)).flexGrow(1.0))
            SpanText(value, Modifier.fontSize(14.px).color(rgb(36, 41, 47)))
        }
        if (!last) Box(Modifier.fillMaxWidth().height(1.px).backgroundColor(rgb(240, 242, 246)))
    }
}

@Composable
private fun StatementBody(ui: StatementsUiState) {
    Box(Modifier.fillMaxWidth().padding(24.px)) {
        when {
            ui.loading -> CenteredLoader()
            ui.error != null -> ErrorBox(ui.error!!)
            else -> StatementTable(ui.rows)
        }
    }
}

@Composable
private fun ErrorBox(msg: String) {
    Row(
        Modifier.fillMaxWidth()
            .backgroundColor(rgb(255, 246, 246))
            .border(1.px, LineStyle.Solid, rgb(255, 205, 205))
            .padding(12.px)
            .borderRadius(12.px)
    ) { SpanText("Greška: $msg") }
}

@Composable
private fun StatementTable(rows: List<StatementRowUi>) {
    val strings = LocalStrings.current
    Column(Modifier.fillMaxWidth()) {
        GridRow(
            modifier = Modifier.tableHeaderRow(),
            invoice = strings.statementHeaderInvoice,
            status = strings.statementHeaderStatus,
            due = strings.statementHeaderDueDate,
            total = strings.statementHeaderTotal,
            isHeader = true
        )

        if (rows.isEmpty()) {
            com.solevo.dist.components.EmptyState(
                title = strings.statementsEmpty,
                message = strings.statementsEmptyMessage
            )
            return@Column
        }

        val context = com.varabyte.kobweb.core.rememberPageContext()
        rows.forEach { r ->
            GridRow(
                modifier = Modifier,
                invoice = r.label,
                status = r.statusText,
                pillHex = r.statusColorHex,
                due = r.dueDateText,
                total = r.totalText,
                isHeader = false,
                onClick = { context.router.navigateTo(com.solevo.dist.navigation.Screen.TransactionDetailsPage.withId(r.id)) }
            )
        }
    }
}

@Composable
private fun GridRow(
    modifier: Modifier = Modifier,
    invoice: String,
    status: String,
    due: String,
    total: String,
    pillHex: String? = null,
    isHeader: Boolean,
    onClick: (() -> Unit)? = null,
) {
    Row(
        modifier
            .fillMaxWidth()
            .display(DisplayStyle.Grid)
            .styleModifier { property("grid-template-columns", "1.2fr 0.6fr 0.8fr 0.8fr") }
            .then(if (isHeader) Modifier.tableHeaderRow() else Modifier.tableRow())
            .then(if (!isHeader && onClick != null) Modifier.cursor(Cursor.Pointer).onClick { onClick() } else Modifier)
    ) {
        SpanText(invoice, if (isHeader) Modifier.tableHeaderCell() else Modifier.tableBodyCellBold())
        if (isHeader) SpanText(status, Modifier.tableHeaderCell()) else Row { StatusPill(status, pillHex ?: "#888888", useIconForInvoiceStatuses = true) }
        SpanText(due, if (isHeader) Modifier.tableHeaderCell() else Modifier.tableBodyCell())
        SpanText(total, if (isHeader) Modifier.tableHeaderCell() else Modifier.tableBodyCell())
    }
}




